import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Peer, MediaConnection } from 'peerjs';
import moment from 'moment';
import { environment } from 'src/environments/environment';
import { ProjectService } from 'src/app/Services/project.service';
import { TokenEncryptionService } from 'src/app/Services/token-encryption.service';
import { LiveScreencastQuotaService } from 'src/app/Services/live-screencast-quota.service';
import { LiveScreencastQuotaUiService } from 'src/app/Services/live-screencast-quota-ui.service';
import {
  isLiveScreencastQuotaError,
  LIVE_SCREENCAST_QUOTA_EXHAUSTED_MESSAGE,
} from 'src/app/entitlements/live-screencast-quota.util';

export type LiveTileMode = 'single' | 'grid' | 'wall' | 'patrol';
export type LiveTileStatus =
  | 'empty'
  | 'connecting'
  | 'live'
  | 'idle'
  | 'stale'
  | 'failed'
  | 'offline'
  | 'unavailable'
  | 'permission_denied'
  | 'ended';

export type LiveStreamEndReason =
  | 'admin_closed'
  | 'employee_switched'
  | 'tile_removed'
  | 'tile_replaced'
  | 'mode_changed'
  | 'page_closed'
  | 'route_changed'
  | 'browser_unload'
  | 'connection_failed'
  | 'stream_stale'
  | 'patrol_next'
  | 'patrol_previous'
  | 'patrol_paused'
  | 'patrol_stopped'
  | 'offline_timeout'
  | 'system_cleanup'
  | 'logout';

export type LiveTileState = {
  tileId: string;
  employeeId: number | null;
  employee: any | null;
  watcherUserId: number | null;
  companyId: number | null;
  mode: LiveTileMode;
  layoutSize?: 1 | 2 | 4 | 16;
  qualityProfile?: 'standard' | 'grid' | 'preview' | 'fullscreen';
  status: LiveTileStatus;
  startedAtUtc?: string;
  endedAtUtc?: string;
  lastFrameAtUtc?: string;
  auditSessionId?: string;
  errorMessage?: string;
  retryCount: number;
  mediaStream?: MediaStream | null;
};

type StartArgs = {
  tileId: string;
  employee: any;
  mode: LiveTileMode;
  layoutSize: 1 | 2 | 4 | 16;
  endReasonOnReplace: LiveStreamEndReason;
};

type ActiveSession = {
  tileId: string;
  sessionId: string;
  employeeId: number;
  call: MediaConnection;
  connectionStartMs: number | null;
  mode: LiveTileMode;
  layoutSize: number;
};

type QueuedStart = {
  tileId: string;
  remotePeerId: string;
  args: StartArgs;
  auditSessionId: string;
};

@Injectable({ providedIn: 'root' })
export class LiveStreamManagerService {
  private readonly peerExpireKey = 'peerExpire';
  private readonly peerIdKey = 'peerId';

  private peer: Peer | null = null;
  private peerReady: Promise<void> | null = null;
  private pendingStarts: QueuedStart[] = [];
  private inFlightStarts = 0;
  private readonly maxInFlightStarts = 3;
  private activeStartTiles = new Set<string>();
  private heartbeatTimers = new Map<string, ReturnType<typeof setInterval>>();

  private tilesSubject = new BehaviorSubject<Record<string, LiveTileState>>({});
  tiles$ = this.tilesSubject.asObservable();

  private sessionsSubject = new BehaviorSubject<ActiveSession[]>([]);
  sessions$ = this.sessionsSubject.asObservable();

  constructor(
    private projectService: ProjectService,
    private tokenEncryption: TokenEncryptionService,
    private liveScreencastQuota: LiveScreencastQuotaService,
    private quotaUi: LiveScreencastQuotaUiService
  ) {
    this.initEmptyTiles();
    this.liveScreencastQuota.quotaExhaustedDuringWatch$.subscribe(() => {
      if (!this.liveScreencastQuota.isWatching()) {
        return;
      }
      this.stopAllStreams('system_cleanup');
      this.quotaUi.promptQuotaUpgrade();
    });
  }

  getTile(tileId: string): LiveTileState | null {
    const map = this.tilesSubject.getValue();
    return map && map[tileId] ? map[tileId] : null;
  }

  getTilesSnapshot(): Record<string, LiveTileState> {
    return this.tilesSubject.getValue();
  }

  getActiveTileIdsForEmployee(employeeId: number): string[] {
    if (!employeeId) return [];
    return Object.values(this.tilesSubject.getValue())
      .filter((t) => !!t && t.employeeId === employeeId && t.status !== 'empty' && t.status !== 'ended')
      .map((t) => t.tileId);
  }

  isTileActive(tileId: string): boolean {
    const t = this.getTile(tileId);
    return !!t && t.status !== 'empty' && t.status !== 'ended';
  }

  getFirstActiveTileId(tileIds: string[]): string | null {
    for (const id of tileIds) {
      if (this.isTileActive(id)) return id;
    }
    return null;
  }

  gridTileIds(size: 2 | 4): string[] {
    return size === 2 ? ['g1', 'g2'] : ['g1', 'g2', 'g3', 'g4'];
  }

  wallTileIds(size: 16): string[] {
    return Array.from({ length: size }, (_, i) => `w${i + 1}`);
  }

  pickNextFreeTile(size: 2 | 4): string | null {
    const map = this.tilesSubject.getValue();
    for (const id of this.gridTileIds(size)) {
      const t = map[id];
      if (!t || t.status === 'empty' || t.status === 'ended') return id;
    }
    return null;
  }

  pickNextFreeWallTile(size: 16): string | null {
    const map = this.tilesSubject.getValue();
    for (const id of this.wallTileIds(size)) {
      const t = map[id];
      if (!t || t.status === 'empty' || t.status === 'ended') return id;
    }
    return null;
  }

  startStream(args: StartArgs) {
    const employeeId = Number(args.employee?.id);
    const remotePeerId = args.employee?.meta?.callingid;
    const companyId = this.tokenEncryption.getEncryptedItem<any>('logedInUser')?.company_id ?? null;
    const watcherUserId = this.tokenEncryption.getEncryptedItem<any>('logedInUser')?.id ?? null;

    // Cleanup any existing stream on the same tile before starting.
    this.stopStream(args.tileId, args.endReasonOnReplace);

    const auditSessionId = this.newSessionId();
    const tile: LiveTileState = {
      tileId: args.tileId,
      employeeId: employeeId || null,
      employee: args.employee,
      watcherUserId,
      companyId,
      mode: args.mode,
      layoutSize: args.layoutSize,
      status: remotePeerId ? 'connecting' : 'unavailable',
      auditSessionId,
      retryCount: 0,
      mediaStream: null,
    };
    this.setTile(tile);

    if (this.liveScreencastQuota.isQuotaExhausted()) {
      this.setTile({
        ...tile,
        status: 'failed',
        errorMessage: LIVE_SCREENCAST_QUOTA_EXHAUSTED_MESSAGE,
      });
      this.quotaUi.promptQuotaUpgrade();
      return;
    }

    if (!remotePeerId) {
      this.logAuditEvent({
        session_id: auditSessionId,
        company_id: companyId,
        employee_user_id: employeeId,
        event_type: 'FAILED',
        event_time_utc: moment.utc().format('YYYY-MM-DD HH:mm:ss'),
        failure_stage: 'missing_callingid',
        end_reason: 'UNAVAILABLE',
        source: 'live_monitoring',
        mode: args.mode,
        layout_size: args.layoutSize,
        tile_id: args.tileId,
      });
      return;
    }

    this.ensurePeerReady()
      .then(() => {
        this.enqueueStart({
          tileId: args.tileId,
          remotePeerId: remotePeerId.toString(),
          args,
          auditSessionId,
        });
      })
      .catch((e) => {
        this.setTile({ ...tile, status: 'failed', errorMessage: 'Could not init peer' });
      });
  }

  /**
   * Best-effort retry: restart a tile using its current employee + layout context.
   * Keeps behavior simple; deeper state-machine improvements come in the hardening step.
   */
  retryTile(tileId: string) {
    const t = this.getTile(tileId);
    if (!t?.employee) return;
    const mode = (t.mode || 'single') as LiveTileMode;
    const layoutSize = (t.layoutSize || (mode === 'wall' ? 16 : 1)) as any;
    this.startStream({
      tileId,
      employee: t.employee,
      mode,
      layoutSize,
      endReasonOnReplace: 'tile_replaced',
    });
  }

  moveSession(
    fromTileId: string,
    toTileId: string,
    mode: LiveTileMode,
    layoutSize: 1 | 2 | 4 | 16,
    endReasonOnReplace: LiveStreamEndReason = 'tile_replaced'
  ): boolean {
    if (fromTileId === toTileId) return this.isTileActive(fromTileId);
    const fromTile = this.getTile(fromTileId);
    if (!fromTile || fromTile.status === 'empty' || fromTile.status === 'ended') return false;

    // Clear destination tile/session first.
    this.stopStream(toTileId, endReasonOnReplace);

    // Move any active media session to new tile id.
    const sessions = [...this.sessionsSubject.getValue()];
    const sessIdx = sessions.findIndex((s) => s.tileId === fromTileId);
    if (sessIdx >= 0) {
      sessions[sessIdx] = {
        ...sessions[sessIdx],
        tileId: toTileId,
        mode,
        layoutSize,
      };
      this.sessionsSubject.next(sessions);
    }

    const map = { ...this.tilesSubject.getValue() };
    map[toTileId] = {
      ...fromTile,
      tileId: toTileId,
      mode,
      layoutSize,
    };
    map[fromTileId] = this.emptyTile(fromTileId, this.modeFromTileId(fromTileId));
    this.tilesSubject.next(map);
    return true;
  }

  /**
   * Migrate all active sessions from one set of tiles to another in a single,
   * predictable batch. Preserves relative ordering: the first active source
   * tile lands on the first destination tile, and so on. Extra active sources
   * beyond `targetTileIds.length` are closed with `overflowReason`.
   *
   * Returns the destination tile id of the first moved session (if any) so
   * callers can highlight the new focus.
   */
  migrateSessions(
    sourceTileIds: string[],
    targetTileIds: string[],
    mode: LiveTileMode,
    layoutSize: 1 | 2 | 4 | 16,
    overflowReason: LiveStreamEndReason = 'mode_changed'
  ): string | null {
    if (!sourceTileIds?.length || !targetTileIds?.length) return null;

    const activeSources = sourceTileIds.filter((id) => this.isTileActive(id));
    if (!activeSources.length) return null;

    // Free target slots from any prior occupants so the move never collides.
    for (const id of targetTileIds) {
      if (!sourceTileIds.includes(id)) this.stopStream(id, 'tile_replaced');
    }

    let firstAssigned: string | null = null;
    const usedTargets = new Set<string>();
    const used = new Set<string>();

    for (let i = 0; i < activeSources.length; i++) {
      const src = activeSources[i];
      const dst = targetTileIds[i];
      if (!dst) break;

      if (src === dst) {
        // Already in place; just update mode/layout metadata.
        const t = this.getTile(src);
        if (t) {
          const map = { ...this.tilesSubject.getValue() };
          map[src] = { ...t, mode, layoutSize };
          this.tilesSubject.next(map);
          const sessions = [...this.sessionsSubject.getValue()];
          const sessIdx = sessions.findIndex((s) => s.tileId === src);
          if (sessIdx >= 0) {
            sessions[sessIdx] = { ...sessions[sessIdx], mode, layoutSize };
            this.sessionsSubject.next(sessions);
          }
        }
      } else {
        this.moveSession(src, dst, mode, layoutSize, 'tile_replaced');
      }

      usedTargets.add(dst);
      used.add(src);
      if (!firstAssigned) firstAssigned = dst;
    }

    // Close any leftover source streams that couldn't be carried over.
    for (const src of activeSources) {
      if (!used.has(src) && !usedTargets.has(src)) {
        this.stopStream(src, overflowReason);
      }
    }

    return firstAssigned;
  }

  /**
   * Pack active streams into the first slots of a grid/wall (w1, w2, …) so
   * removing a tile does not leave holes in the layout.
   */
  compactLayoutTiles(
    tileIds: string[],
    mode: LiveTileMode,
    layoutSize: 1 | 2 | 4 | 16
  ): string | null {
    if (!tileIds.some((id) => this.isTileActive(id))) return null;
    return this.migrateSessions(tileIds, tileIds, mode, layoutSize, 'tile_removed');
  }

  stopStream(tileId: string, reason: LiveStreamEndReason) {
    // Remove any queued start for this tile so a stale queued connect doesn't reopen it.
    this.pendingStarts = this.pendingStarts.filter((q) => q.tileId !== tileId);

    const sessions = this.sessionsSubject.getValue();
    const sess = sessions.find((s) => s.tileId === tileId);
    if (sess) {
      this.liveScreencastQuota.unregisterActiveWatch(`live:${sess.sessionId}`);
      try {
        sess.call.close();
      } catch { }
      this.stopHeartbeat(sess.sessionId);
      this.sessionsSubject.next(sessions.filter((s) => s.tileId !== tileId));
      this.logAuditEvent({
        session_id: sess.sessionId,
        company_id: this.tokenEncryption.getEncryptedItem<any>('logedInUser')?.company_id ?? null,
        employee_user_id: sess.employeeId,
        event_type: 'ENDED',
        event_time_utc: moment.utc().format('YYYY-MM-DD HH:mm:ss'),
        end_reason: reason,
        source: 'live_monitoring',
        mode: sess.mode,
        layout_size: sess.layoutSize,
        tile_id: tileId,
      });
    }

    this.setTile(this.emptyTile(tileId, this.modeFromTileId(tileId)));
    this.releaseStartSlot(tileId);
  }

  stopAllStreams(reason: LiveStreamEndReason) {
    const sessions = [...this.sessionsSubject.getValue()];
    for (const s of sessions) {
      this.stopStream(s.tileId, reason);
    }
  }

  stopAllExcept(tileIdsToKeep: string[], reason: LiveStreamEndReason) {
    const keep = new Set(tileIdsToKeep);
    const sessions = [...this.sessionsSubject.getValue()];
    for (const s of sessions) {
      if (!keep.has(s.tileId)) this.stopStream(s.tileId, reason);
    }
  }

  private initEmptyTiles() {
    const base: Record<string, LiveTileState> = {
      single: this.emptyTile('single', 'single'),
      patrol: this.emptyTile('patrol', 'patrol'),
      g1: this.emptyTile('g1', 'grid'),
      g2: this.emptyTile('g2', 'grid'),
      g3: this.emptyTile('g3', 'grid'),
      g4: this.emptyTile('g4', 'grid'),
    };
    for (const id of this.wallTileIds(16)) {
      base[id] = this.emptyTile(id, 'wall');
    }
    this.tilesSubject.next(base);
  }

  private emptyTile(tileId: string, mode: LiveTileMode): LiveTileState {
    return {
      tileId,
      employeeId: null,
      employee: null,
      watcherUserId: null,
      companyId: null,
      mode,
      status: 'empty',
      retryCount: 0,
      mediaStream: null,
    };
  }

  private modeFromTileId(tileId: string): LiveTileMode {
    if (tileId === 'single') return 'single';
    if (tileId === 'patrol') return 'patrol';
    if (tileId.startsWith('g')) return 'grid';
    return 'wall';
  }

  private setTile(tile: LiveTileState) {
    const map = { ...this.tilesSubject.getValue(), [tile.tileId]: tile };
    this.tilesSubject.next(map);
  }

  private normalizePeerJsPath(path: any): string | undefined {
    if (!path) return undefined;
    const p = String(path).trim();
    if (!p) return undefined;
    // PeerJS appends `/peerjs` internally. If someone configures `/peerjs` here,
    // the resulting URL becomes `/peerjs/peerjs` which breaks signaling.
    if (p === '/peerjs' || p === '/peerjs/') return '/';
    return p;
  }

  private getPeerJsOptions(): any {
    const cfg: any = {
      config: {
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { url: 'stun:stun.raoinfo.tech:3478' as any },
          { url: 'turn:stun.raoinfo.tech:3478' as any, username: 'raoinfotech', credential: 'Raoinfotech123' },
        ],
      },
    };

    const e: any = environment as any;
    const peerCfg = e?.peerjs || {};
    if (peerCfg?.host) {
      cfg.host = peerCfg.host;
      if (peerCfg.port) cfg.port = Number(peerCfg.port);
      const normalizedPath = this.normalizePeerJsPath(peerCfg.path);
      if (normalizedPath) cfg.path = normalizedPath;
      if (typeof peerCfg.secure === 'boolean') cfg.secure = peerCfg.secure;
      if (peerCfg.key) cfg.key = peerCfg.key;
    }
    return cfg;
  }

  private getPeerJsFallbackOptions(): any {
    return {
      config: {
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { url: 'stun:stun.raoinfo.tech:3478' as any },
          { url: 'turn:stun.raoinfo.tech:3478' as any, username: 'raoinfotech', credential: 'Raoinfotech123' },
        ],
      },
    };
  }

  private debugPeer(label: string) {
    try {
      const e: any = environment as any;
      const peerCfg = e?.peerjs || {};
      const safePeerCfg = peerCfg?.host ? {
        host: peerCfg.host,
        port: peerCfg.port,
        path: peerCfg.path,
        secure: peerCfg.secure,
        key: peerCfg.key ? '(set)' : '(default)',
      } : { host: '(default peerjs.com)' };
      // eslint-disable-next-line no-console
      console.info(`[LiveMonitoring][PeerJS] ${label}`, { peerId: sessionStorage.getItem(this.peerIdKey), peer: safePeerCfg });
    } catch { }
  }

  private ensurePeerReady(): Promise<void> {
    if (this.peerReady) return this.peerReady;
    this.peerReady = new Promise<void>((resolve, reject) => {
      try {
        const id = this.getOrCreatePeerId();
        let settled = false;
        let fallbackAttempted = false;
        let openTimer: any = null;
        const armOpenTimer = () => {
          try { if (openTimer) clearTimeout(openTimer); } catch { }
          openTimer = setTimeout(() => {
            if (settled) return;
            if (!fallbackAttempted) {
              fallbackAttempted = true;
              try { this.peer?.destroy(); } catch { }
              bind(new Peer(id, this.getPeerJsFallbackOptions()));
              armOpenTimer();
              return;
            }
            settleReject(new Error('peer_open_timeout'));
          }, 8000);
        };
        const settleResolve = () => {
          if (settled) return;
          settled = true;
          try { if (openTimer) clearTimeout(openTimer); } catch { }
          resolve();
        };
        const settleReject = (err: any) => {
          if (settled) return;
          settled = true;
          try { if (openTimer) clearTimeout(openTimer); } catch { }
          reject(err);
        };
        const bind = (p: Peer) => {
          this.peer = p;
          this.debugPeer(fallbackAttempted ? 'created (fallback default peerjs cloud)' : 'created (primary signaling)');
          p.on('open', (openId: any) => {
            // eslint-disable-next-line no-console
            console.info('[LiveMonitoring][PeerJS] open', { id: openId, fallbackAttempted });
            settleResolve();
          });
          p.on('disconnected', () => {
            // eslint-disable-next-line no-console
            console.warn('[LiveMonitoring][PeerJS] disconnected');
          });
          p.on('close', () => {
            // eslint-disable-next-line no-console
            console.warn('[LiveMonitoring][PeerJS] close');
          });
          p.on('error', (err: any) => {
            // eslint-disable-next-line no-console
            console.error('[LiveMonitoring][PeerJS] error', err);
            const errType = String(err?.type || '').toLowerCase();
            const canFallback =
              !fallbackAttempted &&
              !settled &&
              (errType.includes('network') || errType.includes('socket') || errType.includes('server') || errType.includes('unavailable'));
            if (canFallback) {
              fallbackAttempted = true;
              try { p.destroy(); } catch { }
              bind(new Peer(id, this.getPeerJsFallbackOptions()));
              armOpenTimer();
              return;
            }
            settleReject(err);
          });
        };

        bind(new Peer(id, this.getPeerJsOptions()));
        armOpenTimer();
      } catch (e) {
        reject(e);
      }
    });
    return this.peerReady;
  }

  private getOrCreatePeerId(): string {
    const existing = sessionStorage.getItem(this.peerIdKey);
    const exp = sessionStorage.getItem(this.peerExpireKey);
    const isExpired = !exp || moment().isAfter(moment(exp));
    if (existing && !isExpired) return existing;
    const companyId = this.tokenEncryption.getEncryptedItem<any>('logedInUser')?.company_id ?? 0;
    const safeCompanyId = Number(companyId) || 0;
    const next = `${environment.ADMIN_PEER_ID}-${safeCompanyId}-${this.newSessionId()}`;
    sessionStorage.setItem(this.peerIdKey, next);
    sessionStorage.setItem(this.peerExpireKey, moment().add(1, 'hours').format());
    return next;
  }

  private async doCall(tileId: string, remotePeerId: string, args: StartArgs) {
    const peer = this.peer;
    if (!peer) throw new Error('peer_not_ready');

    const sessionId = this.getTile(tileId)?.auditSessionId || this.newSessionId();
    this.setTile({
      ...this.tilesSubject.getValue()[tileId],
      auditSessionId: sessionId,
      status: 'connecting',
      errorMessage: undefined,
    });

    const isCurrentSession = () => this.getTile(tileId)?.auditSessionId === sessionId;

    const companyId = this.tokenEncryption.getEncryptedItem<any>('logedInUser')?.company_id ?? null;
    const employeeId = Number(args.employee?.id);

    // STARTED audit (reuse same endpoint; extra metadata is non-blocking)
    this.logAuditEvent({
      session_id: sessionId,
      company_id: companyId,
      employee_user_id: employeeId,
      event_type: 'STARTED',
      event_time_utc: moment.utc().format('YYYY-MM-DD HH:mm:ss'),
      admin_browser_info: this.getBrowserInfo(),
      admin_device_agent_info: this.getOsLabel(),
      source: 'live_monitoring',
      mode: args.mode,
      layout_size: args.layoutSize,
      tile_id: tileId,
    });

    const dummy = this.createDummyStream();
    const call = peer.call(remotePeerId.toString(), dummy);
    this.activeStartTiles.add(tileId);
    let startSettled = false;
    const settleStart = () => {
      if (startSettled) return;
      startSettled = true;
      this.releaseStartSlot(tileId);
    };
    const sess: ActiveSession = {
      tileId,
      sessionId,
      employeeId,
      call,
      connectionStartMs: Date.now(),
      mode: args.mode,
      layoutSize: args.layoutSize,
    };
    this.sessionsSubject.next([...this.sessionsSubject.getValue(), sess]);

    // Mode-aware connect timeout: wall/grid need longer startup windows under parallel load.
    const connectTimeoutMs =
      args.mode === 'wall' ? 35000 :
        args.mode === 'grid' ? 25000 : 15000;
    const connectDeadlineMs = Date.now() + connectTimeoutMs;
    const timeoutId = setTimeout(() => {
      const cur = this.getTile(tileId);
      if (!cur || cur.status !== 'connecting' || !isCurrentSession()) return;
      try { call.close(); } catch { }
      this.setTile({
        ...cur,
        status: 'failed',
        errorMessage: 'Timed out connecting',
        mediaStream: null,
      });
      this.logAuditEvent({
        session_id: sessionId,
        company_id: companyId,
        employee_user_id: employeeId,
        event_type: 'FAILED',
        event_time_utc: moment.utc().format('YYYY-MM-DD HH:mm:ss'),
        failure_stage: 'timeout_waiting_stream',
        end_reason: 'connection_failed',
        source: 'live_monitoring',
        mode: args.mode,
        layout_size: args.layoutSize,
        tile_id: tileId,
      });
      settleStart();
    }, Math.max(0, connectDeadlineMs - Date.now()));

    call.on('stream', (remoteStream) => {
      try { clearTimeout(timeoutId); } catch { }
      if (!isCurrentSession()) return;
      this.setTile({
        ...this.tilesSubject.getValue()[tileId],
        status: 'live',
        mediaStream: remoteStream,
        lastFrameAtUtc: moment.utc().format('YYYY-MM-DD HH:mm:ss'),
        startedAtUtc: moment.utc().format('YYYY-MM-DD HH:mm:ss'),
      });
      this.logAuditEvent({
        session_id: sessionId,
        company_id: companyId,
        employee_user_id: employeeId,
        event_type: 'CONNECTED',
        event_time_utc: moment.utc().format('YYYY-MM-DD HH:mm:ss'),
        failure_stage: 'stream_received',
        source: 'live_monitoring',
        mode: args.mode,
        layout_size: args.layoutSize,
        tile_id: tileId,
      });
      this.liveScreencastQuota.registerActiveWatch(`live:${sessionId}`, companyId ?? undefined);
      this.startHeartbeat(sess);
      settleStart();
    });

    call.on('error', (err) => {
      try { clearTimeout(timeoutId); } catch { }
      if (!isCurrentSession()) return;
      this.setTile({
        ...this.tilesSubject.getValue()[tileId],
        status: 'failed',
        errorMessage: err?.message || 'Call error',
        mediaStream: null,
      });
      this.logAuditEvent({
        session_id: sessionId,
        company_id: companyId,
        employee_user_id: employeeId,
        event_type: 'FAILED',
        event_time_utc: moment.utc().format('YYYY-MM-DD HH:mm:ss'),
        failure_stage: 'peer_call_error',
        error_name: err?.name || null,
        error_type: err?.type || null,
        error_message: err?.message || null,
        end_reason: 'connection_failed',
        source: 'live_monitoring',
        mode: args.mode,
        layout_size: args.layoutSize,
        tile_id: tileId,
      });
      settleStart();
    });

    call.on('close', () => {
      try { clearTimeout(timeoutId); } catch { }
      if (!isCurrentSession()) return;
      const cur = this.getTile(tileId);
      if (!cur || cur.status === 'empty') return;
      this.setTile({
        ...cur,
        status: 'ended',
        endedAtUtc: moment.utc().format('YYYY-MM-DD HH:mm:ss'),
        mediaStream: null,
      });
      settleStart();
    });
  }

  private enqueueStart(start: QueuedStart) {
    // Replace any queued item for the same tile with the latest request.
    this.pendingStarts = this.pendingStarts.filter((q) => q.tileId !== start.tileId);
    this.pendingStarts.push(start);
    this.drainStartQueue();
  }

  private drainStartQueue() {
    while (this.inFlightStarts < this.maxInFlightStarts && this.pendingStarts.length > 0) {
      const next = this.pendingStarts.shift();
      if (!next) return;
      const tile = this.getTile(next.tileId);
      // Skip stale queued start if tile was changed/replaced in the meantime.
      if (!tile || tile.auditSessionId !== next.auditSessionId || tile.status !== 'connecting') {
        continue;
      }
      this.inFlightStarts++;
      this.doCall(next.tileId, next.remotePeerId, next.args).catch(() => {
        this.finishStartAttempt();
      });
    }
  }

  private finishStartAttempt() {
    this.inFlightStarts = Math.max(0, this.inFlightStarts - 1);
    this.drainStartQueue();
  }

  private releaseStartSlot(tileId: string) {
    if (!this.activeStartTiles.has(tileId)) return;
    this.activeStartTiles.delete(tileId);
    this.finishStartAttempt();
  }

  private createDummyStream(): MediaStream {
    const canvas = document.createElement('canvas');
    canvas.width = 640;
    canvas.height = 480;
    const videoStream = canvas.captureStream(30);
    const audioContext = new AudioContext();
    const audioStream = audioContext.createMediaStreamDestination().stream;
    return new MediaStream([videoStream.getVideoTracks()[0], audioStream.getAudioTracks()[0]]);
  }

  private newSessionId(): string {
    let d = new Date().getTime();
    return 'xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = ((d + Math.random() * 16) % 16) | 0;
      d = Math.floor(d / 16);
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  private logAuditEvent(payload: any) {
    this.projectService.screencastLogToDb(payload).subscribe({
      error: (err) => {
        if (isLiveScreencastQuotaError(err)) {
          this.handleQuotaExceededFromServer();
        }
      },
    });
  }

  private handleQuotaExceededFromServer(): void {
    this.liveScreencastQuota.refresh().subscribe({
      next: () => this.quotaUi.promptQuotaUpgrade(),
      error: () => this.quotaUi.promptQuotaUpgrade(),
    });
  }

  private startHeartbeat(sess: ActiveSession): void {
    this.stopHeartbeat(sess.sessionId);
    const companyId = this.tokenEncryption.getEncryptedItem<any>('logedInUser')?.company_id ?? null;
    const timerId = setInterval(() => {
      this.logAuditEvent({
        session_id: sess.sessionId,
        company_id: companyId,
        employee_user_id: sess.employeeId,
        event_type: 'HEARTBEAT',
        event_time_utc: moment.utc().format('YYYY-MM-DD HH:mm:ss'),
        source: 'live_monitoring',
        mode: sess.mode,
        layout_size: sess.layoutSize,
        tile_id: sess.tileId,
      });
    }, 60000);
    this.heartbeatTimers.set(sess.sessionId, timerId);
  }

  private stopHeartbeat(sessionId: string): void {
    const timerId = this.heartbeatTimers.get(sessionId);
    if (!timerId) return;
    clearInterval(timerId);
    this.heartbeatTimers.delete(sessionId);
  }

  private getOsLabel(): string {
    const userAgent = window.navigator.userAgent;
    if (userAgent.indexOf('Win') !== -1) return 'Windows';
    if (userAgent.indexOf('Mac') !== -1) return 'MacOS';
    if (userAgent.indexOf('Linux') !== -1) return 'Linux';
    if (userAgent.indexOf('Android') !== -1) return 'Android';
    if (userAgent.indexOf('like Mac') !== -1) return 'iOS';
    return 'Unknown OS';
  }

  private getBrowserInfo(): string {
    const ua = window.navigator.userAgent;
    let match;
    if ((match = ua.match(/edg\/(\d+)/i))) return `Edge ${match[1]}`;
    if ((match = ua.match(/opr\/(\d+)/i))) return `Opera ${match[1]}`;
    if ((match = ua.match(/chrome\/(\d+)/i))) return `Chrome ${match[1]}`;
    if ((match = ua.match(/firefox\/(\d+)/i))) return `Firefox ${match[1]}`;
    if ((match = ua.match(/version\/(\d+).+safari/i))) return `Safari ${match[1]}`;
    return 'Unknown Browser';
  }
}

